import { Component, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FormlyFieldConfig, FormlyForm } from "@ngx-formly/core";
import { posthog } from "posthog-js";
import { ConfirmationService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ConfirmDialog } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { FluidModule } from "primeng/fluid";
import { BehaviorSubject, finalize } from "rxjs";
import { EditUserDialogConfig } from "./edit-user-dialog.types";
import { getEditFormFields } from "./edit-user-form-definition";
import { ApiUpdateUserRequest } from "../../../../apiClient/models";
import { AccessRoleDetails } from "../../../../domain/access-role";
import { OrganizationDetails } from "../../../../domain/organization";
import { UserDetails } from "../../../../domain/user";
import { FormService } from "../../../../services/form.service";
import { OrganizationService } from "../../../../services/organization.service";
import { ToastService } from "../../../../services/toast.service";
import { BrandService } from "../../../../shared/branding/brand.service";
import { primeNgPageModules } from "../../../../shared/modules/prime-ng-common.module";
import { FormlyFormOptionsWithFormState } from "../../../../shared/types/form";
import { UserForSettingsPage } from "../../pages/settings/users/users.component.types";
import { DialogResult } from "../dialog.types";

interface UserEditFormModel {
  firstName: string;
  lastName: string;
  emailAddress: string;
  defaultApprovalGroupId?: number;
  accessRoleId?: number;
}

@Component({
  templateUrl: "./edit-user.dialog.html",
  imports: [
    ...primeNgPageModules,
    ReactiveFormsModule,
    FormlyForm,
    DialogModule,
    ButtonModule,
    FluidModule,
    TranslocoDirective,
    ConfirmDialog,
  ],
  providers: [ConfirmationService],
})
export class EditUserDialog {
  public form = new FormGroup({});
  public model: UserEditFormModel;
  public fields: FormlyFieldConfig[];
  private user: UserForSettingsPage<UserDetails>;
  private organization: OrganizationDetails;
  private requestingUserId: number;
  private _originalAccessRoleId?: number;
  public canModifyUsers: boolean;
  public canRemoveUsers: boolean;
  public options: FormlyFormOptionsWithFormState = {
    formState: {
      disabled: false,
    },
  };
  public loading = false;
  public deleteLoading = new BehaviorSubject<boolean>(false);

  constructor(
    public readonly ref: DynamicDialogRef,
    private readonly formService: FormService,
    private readonly config: DynamicDialogConfig<EditUserDialogConfig>,
    private readonly translocoService: TranslocoService,
    private readonly organizationService: OrganizationService,
    private readonly toastService: ToastService,
    private readonly confirmationService: ConfirmationService,
    private readonly destroyRef: DestroyRef,
    private readonly brandService: BrandService,
  ) {
    this.user = this.config.data!.user;
    this.organization = this.config.data!.organization;
    this.requestingUserId = this.config.data!.requestingUserId;
    this.canModifyUsers = this.config.data!.canModifyUsers;
    this.canRemoveUsers = this.config.data!.canRemoveUsers;
    // When the user can't modify users, open the dialog in read-only mode so the form
    // fields reflect the disabled state via options.formState.
    this.options.formState.disabled = !this.canModifyUsers;
    this.model = {
      firstName: this.user.backingUser.firstName,
      lastName: this.user.backingUser.lastName,
      emailAddress: this.user.backingUser.emailAddress,
      defaultApprovalGroupId: this.user.defaultApprovalGroup?.id ?? undefined,
      accessRoleId: this.user.accessRole?.id,
    };
    this._originalAccessRoleId = this.user.accessRole?.id;

    this.fields = this._buildFormFields(this.organization.accessRoles);
  }

  private _buildFormFields(accessRoles: AccessRoleDetails[]): FormlyFieldConfig[] {
    return this.formService.prepareFormFields(
      getEditFormFields({
        orgData: this.organization,
        companyName: this.brandService.brand().companyName,
        accessRoles,
      }),
    );
  }

  public submitEditUserForm(): void {
    if (!this.form.valid) {
      return;
    }

    this.form.updateValueAndValidity();

    const userData: ApiUpdateUserRequest = {
      accessRoleId: this.model.accessRoleId,
      defaultApprovalGroupId: this.model.defaultApprovalGroupId ?? undefined,
    };

    // Check if data has changed
    if (
      this.user &&
      userData.accessRoleId === this._originalAccessRoleId &&
      userData.defaultApprovalGroupId === this.user.defaultApprovalGroup?.id
    ) {
      this.toastService.info(this.translocoService.translate("app.organization-settings.editUserDialog.noChanges"));
      this.ref.close({ shouldReload: false } as DialogResult);
      return;
    }

    this.loading = true;
    this.options.formState.disabled = true;

    this.organizationService
      .updateOrganizationUser$(this.organization.id, this.user.backingUser.id, userData)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
          this.options.formState.disabled = false;
        }),
      )
      .subscribe({
        next: (_response) => {
          this.toastService.success(
            this.translocoService.translate("app.organization-settings.editUserDialog.success"),
          );

          posthog.capture("user_updated");

          this.ref.close({ shouldReload: true } as DialogResult);
        },
        error: () => {
          this.toastService.error(this.translocoService.translate("app.organization-settings.editUserDialog.error"));
          this.ref.close({ shouldReload: false } as DialogResult);
        },
      });
  }

  public openConfirmationDeleteDialog(): void {
    this.confirmationService.confirm({
      header: this.translocoService.translate("app.organization-settings.editUserDialog.deleteUser"),
      message: this.translocoService.translate("app.organization-settings.editUserDialog.deleteUserConfirmation"),
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: this.translocoService.translate("app.common.cancel"),
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translocoService.translate("app.common.delete"),
        severity: "danger",
      },
      accept: () => {
        const user = this.user.backingUser;
        if (!user) {
          this.toastService.error(this.translocoService.translate("app.common.errors.deleteError"));
          return;
        }

        this.deleteLoading.next(true);
        this.options.formState.disabled = true;

        this.organizationService
          .deleteOrganizationUser$(this.organization.id, user.id)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.deleteLoading.next(false);
              this.options.formState.disabled = false;
            }),
          )
          .subscribe({
            next: () => {
              this.toastService.success(
                this.translocoService.translate("app.organization-settings.editUserDialog.deleteUserSuccess"),
              );

              posthog.capture("user_deleted");

              this.ref.close({ shouldReload: true } as DialogResult);
            },
            error: () => {
              this.toastService.error(this.translocoService.translate("app.common.errors.deleteError"));
            },
          });
      },
    });
  }
}
